import React, { useMemo } from 'react';
import { X, TreePine } from 'lucide-react';
import { buildTimberSupplierSummary } from '../../utils/timberSupplierSummary';
import { collectStructureTimberMeta, formatTimberMoistureLabel } from '../../utils/supplierTimberExport';
import type { ProposalNodeSet } from '../../types';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);

const formatBdft = (n: number) =>
  n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);

export interface TimberSupplierSummaryModalProps {
  nodeSets: ProposalNodeSet[];
  isKitProposal?: boolean;
  activeSpecies?: string;
  onClose: () => void;
}

const TimberSupplierSummaryModal: React.FC<TimberSupplierSummaryModalProps> = ({
  nodeSets,
  isKitProposal,
  activeSpecies,
  onClose,
}) => {
  const summary = useMemo(
    () => buildTimberSupplierSummary(nodeSets, { isKitProposal, activeSpecies }),
    [nodeSets, isKitProposal, activeSpecies]
  );

  return (
    <div
      className="preview-modal-overlay"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '760px',
          width: '94%',
          maxHeight: '90vh',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: '#2C3E2F',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: 'white',
            }}
          >
            <TreePine size={20} /> Timber summary (supplier check)
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close"
          >
            <X size={18} color="white" />
          </button>
        </div>

        <div style={{ padding: '16px 24px 8px', flexShrink: 0 }}>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Required timber by structure (internal line costs), then project totals. Use this to compare a vendor quote
            against the timber loaded in the proposal — not install, freight, or margin.
          </p>
          {isKitProposal && activeSpecies && (
            <p style={{ margin: '10px 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
              Species for this summary: <strong>{activeSpecies}</strong> (matches kit pricing preview).
            </p>
          )}
          {summary.footnote && (
            <p style={{ margin: '10px 0 0', fontSize: '13px', color: 'var(--color-warning-text)', background: 'var(--color-warning-light)', padding: '8px 12px', borderRadius: '8px' }}>
              {summary.footnote}
            </p>
          )}
        </div>

        <div style={{ overflowY: 'auto', padding: '8px 24px 24px', flex: 1 }}>
          {summary.structures.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>No structures in this proposal.</p>
          ) : (
            <>
              {summary.structures.map(st => {
                const structureNs = nodeSets.find((ns) => ns.id === st.structureId);
                const meta = structureNs ? collectStructureTimberMeta(structureNs) : null;
                const gradeHint = meta?.gradeLabels.length ? meta.gradeLabels.join(', ') : null;
                const drynessHint = meta?.drynessLabels.length ? meta.drynessLabels.join(', ') : null;
                return (
                <section key={st.structureId} style={{ marginBottom: '28px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '10px',
                      borderBottom: '1px solid var(--color-border)',
                      paddingBottom: '8px',
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--pm-accent)' }}>
                      {st.structureName}
                      {st.quantity > 1 ? (
                        <span style={{ fontWeight: 500, color: 'var(--color-text-secondary)', marginLeft: '8px' }}>×{st.quantity}</span>
                      ) : null}
                    </h3>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', textAlign: 'right', maxWidth: '420px' }}>
                      <div>Species: {st.speciesLabel}</div>
                      <div style={{ marginTop: '4px' }}>Grade: {gradeHint || '—'}</div>
                      <div style={{ marginTop: '4px' }}>Dryness: {drynessHint || '—'}</div>
                    </div>
                  </div>

                  {st.items.length === 0 ? (
                    <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: '0 0 8px' }}>No timber line items.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ color: 'var(--color-text-secondary)', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px', fontWeight: 600 }}>Piece</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600 }}>Species</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600 }}>Grade</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600 }}>Dryness</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600 }}>Dims</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Qty</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Bd ft ea.</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Bd ft total</th>
                            <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Line $</th>
                          </tr>
                        </thead>
                        <tbody>
                          {st.items.map(row => {
                            const rowBdftTotal = (row.boardFeet || 0) * (row.quantity || 0);
                            return (
                              <tr key={row.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                                <td style={{ padding: '8px', color: 'var(--color-text-primary)' }}>{row.name || '—'}</td>
                                <td style={{ padding: '8px', color: 'var(--color-text-primary)' }}>{row.species || '—'}</td>
                                <td style={{ padding: '8px', color: 'var(--color-text-primary)' }}>{row.grade || '—'}</td>
                                <td style={{ padding: '8px', color: 'var(--color-text-primary)' }}>
                                  {formatTimberMoistureLabel(row.moistureContent) || '—'}
                                </td>
                                <td style={{ padding: '8px', color: 'var(--color-text-primary)', whiteSpace: 'nowrap' }}>{row.dimensions || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{row.quantity || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatBdft(row.boardFeet || 0)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatBdft(rowBdftTotal)}</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(row.total || 0)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginTop: '12px',
                      padding: '12px',
                      background: 'var(--color-surface)',
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Structure timber (material) </span>
                      <strong style={{ color: 'var(--pm-accent)' }}>{formatCurrency(st.timberCostAllUnits)}</strong>
                      {st.quantity > 1 && (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px', marginLeft: '6px' }}>
                          ({formatCurrency(Math.round(st.timberCostAllUnits / st.quantity))} × {st.quantity})
                        </span>
                      )}
                    </div>
                    <div>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Board feet (all units) </span>
                      <strong style={{ color: 'var(--pm-accent)' }}>{formatBdft(st.boardFeetAllUnits)}</strong>
                    </div>
                  </div>
                  {Math.abs(st.timberCostAllUnits - st.lineSumAllUnits) > 2 && st.items.length > 0 && (
                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
                      Note: Line totals ({formatCurrency(st.lineSumAllUnits)}) differ slightly from stored timber total (
                      {formatCurrency(st.timberCostAllUnits)}); stored total is used for pricing.
                    </p>
                  )}
                </section>
              );
              })}

              <div
                style={{
                  marginTop: '8px',
                  padding: '16px 20px',
                  background: '#2C3E2F',
                  color: 'white',
                  borderRadius: '10px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: '16px',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: '12px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Project timber (material)
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginTop: '4px' }}>
                    {formatCurrency(summary.projectTimberCost)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Total board feet
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginTop: '4px' }}>
                    {formatBdft(summary.projectBoardFeet)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimberSupplierSummaryModal;
